import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import { VirtualClinicAnalysisFilterHybridDrg } from './hybrid-drg';


/**
 * The criteria of one group, as a reader wants them.
 *
 * These filters are switched on wholesale in practice, so a configured one
 * reports a run of criteria that all say the same thing — nine of them here,
 * which spelled out one per line is a wall of text that buries the year. A
 * group whose criteria ALL carry the same verdict is therefore written once
 * ("Alle Ausschlüsse erfüllt"), while a MIXED group stays item by item, because
 * there the individual criteria are exactly the information.
 *
 * Criteria that were never set (`null`) are omitted, as they always were: a
 * filter says nothing about what it does not constrain.
 */
function summariseCriteria(
  singular: string,
  plural: string,
  flags: ReadonlyArray<readonly [string, boolean | null]>,
): string[] {
  const set = flags.filter(
    (flag): flag is readonly [string, boolean] => flag[1] !== null,
  );
  if (set.length === 0) return [];

  const verdicts = new Set(set.map(([, value]) => value));
  if (set.length > 1 && verdicts.size === 1) {
    return [`Alle ${plural} ${set[0][1] ? 'erfüllt' : 'nicht erfüllt'}`];
  }

  return set.map(
    ([label, value]) =>
      `${singular} – ${label}: ${value ? 'Erfüllt' : 'Nicht erfüllt'}`,
  );
}

export const HYBRID_DRG_CONDITION_ID = 'hybridDrg';
export const FilterConditionHybridDrgPluginType: FilterConditionPluginSpecification<
  VirtualClinicAnalysisFilterHybridDrg,
  FilterConditionHybridDrgDto,
  HybridDrgCondition
> = {
  type: 'hybridDrg',
  category: 'case',
  displayName: { de: 'Hybrid DRG' },
  sortingIndex: 10,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionHybridDrgDto,
  ): FilterConditionPluginImpl<VirtualClinicAnalysisFilterHybridDrg> {
    return new FilterConditionHybridDrgPlugin(
        VirtualClinicAnalysisFilterHybridDrg.fromObject(obj.hybridDrg),
      );
  },
  fromCondition(
    jsonObj: HybridDrgCondition,
  ): FilterConditionPluginImpl<VirtualClinicAnalysisFilterHybridDrg> {
    return new FilterConditionHybridDrgPlugin(
        VirtualClinicAnalysisFilterHybridDrg.fromObject(jsonObj),
      );
  },
  newInstance(): FilterConditionPluginImpl<VirtualClinicAnalysisFilterHybridDrg> {
    return new FilterConditionHybridDrgPlugin(
        new VirtualClinicAnalysisFilterHybridDrg(),
      );
  },
};

export class FilterConditionHybridDrgPlugin extends FilterConditionPluginImpl<
  VirtualClinicAnalysisFilterHybridDrg,
  FilterConditionHybridDrgDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    VirtualClinicAnalysisFilterHybridDrg,
    FilterConditionHybridDrgDto,
    HybridDrgCondition
  > = FilterConditionHybridDrgPluginType;

  constructor(private _hybridDrg: VirtualClinicAnalysisFilterHybridDrg) {
    super(FilterConditionHybridDrgPluginType.type);
  }

  override clone(): FilterConditionHybridDrgPlugin {
    const hybridDrgClone = this._hybridDrg ? this._hybridDrg.clone() : null;

    return new FilterConditionHybridDrgPlugin(hybridDrgClone);
  }

  override equals(other: FilterConditionHybridDrgPlugin): boolean {
    return (
      other && super.equals(other) && this._hybridDrg.equals(other._hybridDrg)
    );
  }

  override toCondition(): BdCondition | null {
    return new HybridDrgCondition(this._hybridDrg);
  }

  override validationToText(): string {
    return 'Hybrid DRG Kriterien auswählen';
  }

  toJSON(): FilterConditionHybridDrgDto | null {
    return {
      type: this.type,
      hybridDrg: this._hybridDrg.toJSON(),
    };
  }

  override toText(): string | null {
    return `Hybrid DRG Kriterien ${this._hybridDrg.hybridDrgVersionYear}`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title || !this._hybridDrg) return null;

    const d = this._hybridDrg;
    const items: string[] = [`Hybrid DRG Jahr: ${d.hybridDrgVersionYear}`];

    const includes: Array<[string, boolean | null]> = [
      ['OPS §115f Abs. 1 Satz 1 Nr. 2 SGB V', d.ops_p115f_a1_s1_n2_sgb],
    ];
    const excludes: Array<[string, boolean | null]> = [
      ['ICD (stationär)', d.icdNichtAmbulant],
      ['OPS (stationär)', d.opsStationaer],
      ['Funktionseinschränkungen (Barthel-Index)', d.funktionseinschraenkungen],
      ['Pflegegrad', d.pflegegrad],
      ['Beatmung', d.beatmung],
      ['Altersgrenze (> 1 Jahr)', d.altersgrenze],
      ['Beidseitige Eingriffe', d.beidseitig],
      ['Ophthalmologische Begleiterkrankungen', d.ophthalm],
      ['Belegungstage < 2', d.belegungstage_lt_2],
      ['PCCL < 3', d.pccl_lt_3],
      ['Keine Neubildungen', d.keine_neubildungen],
      ['ZE Diagnosen', d.ze_diagnosen],
      ['ZE Prozeduren', d.ze_prozeduren],
    ];

    items.push(...summariseCriteria('Einschluss', 'Einschlüsse', includes));
    items.push(...summariseCriteria('Ausschluss', 'Ausschlüsse', excludes));

    return { title, items };
  }

  override toValue(): VirtualClinicAnalysisFilterHybridDrg {
    return this._hybridDrg;
  }
}

class HybridDrgCondition extends BdCondition {
  constructor(
    private hybridDrg: VirtualClinicAnalysisFilterHybridDrg,
  ) {
    super(HYBRID_DRG_CONDITION_ID);
  }

  override toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        hybridDrgVersionYear: this.hybridDrg.hybridDrgVersionYear,
        ops_p115f_a1_s1_n2_sgb: this.hybridDrg.ops_p115f_a1_s1_n2_sgb,
        icdNichtAmbulant: this.hybridDrg.icdNichtAmbulant,
        opsStationaer: this.hybridDrg.opsStationaer,
        funktionseinschraenkungen: this.hybridDrg.funktionseinschraenkungen,
        pflegegrad: this.hybridDrg.pflegegrad,
        beatmung: this.hybridDrg.beatmung,
        altersgrenze: this.hybridDrg.altersgrenze,
        beidseitig: this.hybridDrg.beidseitig,
        ophthalm: this.hybridDrg.ophthalm,
        belegungstage_lt_2: this.hybridDrg.belegungstage_lt_2,
        pccl_lt_3: this.hybridDrg.pccl_lt_3,
        keine_neubildungen: this.hybridDrg.keine_neubildungen,
        ze_diagnosen: this.hybridDrg.ze_diagnosen,
        ze_prozeduren: this.hybridDrg.ze_prozeduren,
      },
    };
  }
}

interface FilterConditionHybridDrgDto {
  type: string;
  hybridDrg: object;
}
